// #264 — utilitários de detecção de setor de SERVIÇO no frontend.
//
// A regra canônica vive no backend (industry_context.SERVICE_SECTOR_CODES) e o
// modal/PDF consomem o sinal `dados_basicos.is_servico` (sem reimplementar a
// lista). Esta cópia existe para a TELA (NCResultDataSection), que recebe o
// `nc.industria` cru e não passa pelo report_service_v2. Mantém códigos E nomes
// de exibição (alguns NCs antigos gravaram o display name em vez do código).

const SERVICE_SECTOR_VALUES = new Set([
  // códigos canônicos
  'contabil', 'saude', 'logistica', 'facilities', 'laboratorios', 'ti',
  'engenharia_projetos', 'servicos',
  // nomes de exibição (NCs que gravaram o display name)
  'contábil / fiscal', 'contabil / fiscal',
  'saúde / clínicas', 'saude / clinicas',
  'logística / armazenagem (3pl)', 'logistica / armazenagem (3pl)',
  'facilities / terceirizados',
  'laboratórios (calibração/ensaios)', 'laboratorios (calibracao/ensaios)',
  'ti / software',
  'engenharia / projetos',
  'serviços', 'servicos',
]);

/**
 * True se a indústria/setor da NC é de SERVIÇO (não tem produto/lote físico).
 * Aceita código ("ti") ou nome de exibição ("TI / Software"), case-insensitive.
 * null/vazio → false (conservador: trata como industrial).
 */
export const isServiceSector = (industria) =>
  industria != null &&
  SERVICE_SECTOR_VALUES.has(String(industria).toLowerCase().trim());

/**
 * True se o valor de um campo físico (produto/lote) é vazio ou um placeholder
 * de "não preenchido" (ex.: "Não identificado"). Usado para decidir se um campo
 * físico deve ser omitido num relatório de setor de serviço.
 */
export const isPlaceholderFisico = (valor) => {
  if (valor == null) return true;
  const t = String(valor).trim().toLowerCase();
  return (
    t === '' ||
    t === 'n/a' ||
    t === 'na' ||
    t === 'não identificado' ||
    t === 'nao identificado' ||
    t === 'não informado' ||
    t === 'nao informado' ||
    t === '-' ||
    t === '—'
  );
};

/**
 * #264b — rótulos dos campos físicos (Produto / Lote) adaptados ao tipo de NC.
 *
 * O #264 OCULTAVA Produto/Lote quando vazios num setor de serviço; esta função
 * complementa: quando o campo está PREENCHIDO num serviço, "Produto"/"Lote" não
 * fazem sentido (um serviço não tem produto físico nem lote) — então renomeia
 * para termos de serviço. Setor industrial: rótulos inalterados.
 *
 * Centraliza as strings num único lugar para as 4 superfícies (modal, PDF body,
 * cabeçalho do PDF, tela) não divergirem.
 *
 * @param {boolean} isServico - true se a NC é de setor de serviço.
 * @returns {{produto: string, lote: string}} rótulos a exibir.
 */
export const labelsFisicos = (isServico) =>
  isServico
    ? { produto: 'Serviço / Processo', lote: 'Período / Referência' }
    : { produto: 'Produto', lote: 'Lote' };

/** Rótulo do campo "Produto" — 'Serviço / Processo' em serviço, senão 'Produto'. */
export const labelProdutoFisico = (isServico) => labelsFisicos(isServico).produto;

/** Rótulo do campo "Lote" — 'Período / Referência' em serviço, senão 'Lote'. */
export const labelLoteFisico = (isServico) => labelsFisicos(isServico).lote;
